import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CustomJsonViewer } from '@src/components';
import { colors } from '@src/styles';
import { useRunsContext } from '../context/RunsContext';
import { StatusChip } from './statusChip';
import { getTaskIdByRequestId } from '../hooks/runs';
import { monoFontFamily, monoLabelSX, skeletonSX, titleFontFamily } from '../utils/format';

// NOTE: `RequestGetByIdResponseDTO.run` is typed as a bare `object | null` in the
// real DTO (src/services/automations/modules/v1/requests/dto/requests.getById.response.dto.ts).
// No consumer in this codebase declares a concrete interface for its contents; the only other
// place that reads it (Tasks/components/TaskDetailsPanel.tsx) does a defensive `'context' in run`
// check before touching `run.context` and goes no deeper (no per-block status map is read anywhere).
// The shape below (status/context.status/context.data/createdAt/finishedAt) is therefore an
// UNCONFIRMED assumption carried over from the task brief. All access is optional-chained so a
// mismatched real shape degrades gracefully (empty block list, but the full JSON viewer below
// still renders whatever `run` actually contains).
type AssumedRunShape = {
  status?: string;
  context?: { data?: Record<string, unknown>; status?: Record<string, string> };
  createdAt?: string;
  finishedAt?: string;
};

const BLOCK_STATUS_COLOR: Record<string, string> = {
  SUCCESS: colors.status.concluded,
  ERROR: colors.status.rejected,
};

const sectionLabelSX = { ...monoLabelSX, mb: 1.25 } as const;

const actionButtonSX = {
  height: 36,
  px: 1.75,
  borderRadius: '8px',
  fontFamily: titleFontFamily,
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  '& .MuiButton-startIcon svg': { fontSize: 17, color: 'inherit' },
} as const;

const DrawerSkeleton = () => (
  <Box aria-busy="true" aria-label="Carregando execução" sx={{ p: 2.5 }}>
    <Skeleton variant="text" width={70} height={14} sx={skeletonSX} />
    <Skeleton variant="text" width="65%" height={30} sx={skeletonSX} />
    <Skeleton variant="rounded" width={90} height={22} sx={{ ...skeletonSX, borderRadius: '999px', mt: 1.5 }} />
    {[0, 1, 2].map((item) => (
      <Box key={item} sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
        <Skeleton variant="text" width={70} height={18} sx={skeletonSX} />
        <Skeleton variant="text" width="55%" height={18} sx={skeletonSX} />
      </Box>
    ))}
    <Skeleton variant="text" width={150} height={14} sx={{ ...skeletonSX, mt: 4, mb: 1 }} />
    {[0, 1, 2, 3].map((item) => (
      <Skeleton key={item} variant="rounded" height={44} sx={{ ...skeletonSX, borderRadius: '10px', mb: 1 }} />
    ))}
  </Box>
);

export function RunDetailsDrawer() {
  const { selectedRunId, selectedRun, isLoadingRun, setSelectedRunId, createRerun, organizationId, runs } =
    useRunsContext();
  const [snack, setSnack] = useState<string | null>(null);
  const [isRerunLoading, setIsRerunLoading] = useState(false);
  const navigate = useNavigate();
  const open = !!selectedRunId;

  const run = (selectedRun?.run ?? null) as AssumedRunShape | null;
  const rowStatus = runs?.items.find((i) => i.id === selectedRunId)?.runStatus;
  const status = run?.status ?? rowStatus ?? 'RUNNING';
  const blocks = run?.context?.status ? Object.keys(run.context.status) : [];

  const handleRerun = async () => {
    setIsRerunLoading(true);
    try {
      await createRerun();
      setSnack('Rerun solicitado.');
    } catch {
      setSnack('Falha ao solicitar rerun.');
    } finally {
      setIsRerunLoading(false);
    }
  };
  const handleSeeTask = async () => {
    if (!selectedRun) return;
    try {
      const taskId = await getTaskIdByRequestId(selectedRun.id, organizationId);
      navigate(taskId ? `/tasks?taskId=${taskId}` : '/tasks');
    } catch {
      setSnack('Falha ao buscar a task de erro.');
      navigate('/tasks');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setSelectedRunId(null)}
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(10, 13, 15, 0.5)' } },
      }}
      PaperProps={{
        sx: {
          width: 'min(560px, 94vw)',
          backgroundColor: colors.brand.surface,
          backgroundImage: 'none',
          color: colors.primary.white,
          borderLeft: `1px solid ${colors.brand.line}`,
          boxShadow: '-24px 0 60px rgba(0, 0, 0, 0.4)',
          p: 0,
        },
      }}
    >
      {isLoadingRun && <DrawerSkeleton />}
      {!isLoadingRun && selectedRun && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${colors.brand.line}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={monoLabelSX}>Execução</Typography>
                <Typography
                  sx={{
                    fontFamily: titleFontFamily,
                    fontWeight: 600,
                    fontSize: '18px',
                    letterSpacing: '-0.02em',
                    mt: 0.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedRun.flowName ?? selectedRun.canonicalFlowKey ?? selectedRun.id}
                </Typography>
              </Box>
              <Tooltip title="Fechar" arrow>
                <IconButton
                  onClick={() => setSelectedRunId(null)}
                  aria-label="Fechar detalhes da execução"
                  sx={{
                    width: 32,
                    height: 32,
                    color: colors.brand.textSecondary,
                    '& svg': { color: 'inherit' },
                    '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <StatusChip status={status} />
            </Box>
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                gap: '8px 12px',
                mt: 2,
                mb: 0,
                '& dt': { ...monoLabelSX, alignSelf: 'center' },
                '& dd': {
                  margin: 0,
                  fontFamily: monoFontFamily,
                  fontSize: '12px',
                  color: colors.brand.textStrong,
                  wordBreak: 'break-all',
                },
              }}
            >
              <dt>Run ID</dt>
              <dd>{selectedRun.runId ?? '—'}</dd>
              <dt>Início</dt>
              <dd>{run?.createdAt ? new Date(run.createdAt).toLocaleString('pt-BR') : '—'}</dd>
              <dt>Fim</dt>
              <dd>{run?.finishedAt ? new Date(run.finishedAt).toLocaleString('pt-BR') : '—'}</dd>
            </Box>
          </Box>

          <Box sx={{ p: 2.5, overflow: 'auto', flex: 1 }}>
            <Typography sx={sectionLabelSX}>Blocos do fluxo · {blocks.length} passos</Typography>
            {blocks.map((k) => {
              const s = run?.context?.status?.[k];
              const dot = (s && BLOCK_STATUS_COLOR[s]) ?? colors.brand.textMuted;
              return (
                <Box
                  key={k}
                  sx={{
                    border: `1px solid ${colors.brand.line}`,
                    borderRadius: '10px',
                    mb: 1,
                    overflow: 'hidden',
                    backgroundColor: colors.background.primary,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      borderBottom: `1px solid ${colors.brand.line}`,
                    }}
                  >
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, minWidth: 0, fontFamily: monoFontFamily, fontSize: '12.5px' }}>
                      {k}
                    </Typography>
                    <Typography sx={{ ...monoLabelSX, color: dot }}>{s}</Typography>
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <CustomJsonViewer data={run?.context?.data?.[k]} />
                  </Box>
                </Box>
              );
            })}
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={sectionLabelSX}>Contexto completo (JSON)</Typography>
              <CustomJsonViewer data={run?.context} />
            </Box>
          </Box>

          {status === 'FAILED' && (
            <Box sx={{ p: 2, borderTop: `1px solid ${colors.brand.line}`, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disableElevation
                onClick={handleRerun}
                disabled={isRerunLoading}
                startIcon={isRerunLoading ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                sx={{
                  ...actionButtonSX,
                  backgroundColor: colors.primary.yellow,
                  color: colors.brand.ink,
                  '&:hover': { backgroundColor: '#FFB94A' },
                  '&.Mui-disabled': { backgroundColor: 'rgba(255, 160, 21, 0.3)', color: 'rgba(10, 13, 15, 0.6)' },
                }}
              >
                Criar Rerun
              </Button>
              <Button
                variant="outlined"
                onClick={handleSeeTask}
                startIcon={<OpenInNewIcon />}
                sx={{
                  ...actionButtonSX,
                  color: colors.brand.textStrong,
                  borderColor: colors.brand.line,
                  '&:hover': { borderColor: colors.primary.yellow, backgroundColor: 'transparent' },
                }}
              >
                Ver Task de erro
              </Button>
            </Box>
          )}
          <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack ?? ''} />
        </Box>
      )}
    </Drawer>
  );
}
